import React, { useEffect } from "react";
import { Dashboard } from "./components/Dashboard/Dashboard";
import VpaUploadPage from "./components/Vpa/VpaUploadPage";
import DistributorList from "./components/Wallet/DistributorListPage";
import { Sidebar } from "./components/SidebarComponent/Sidebar";
import DistributorReconReport from "./components/ReconReport/DistributorReconReport";
import Login from "./components/Login/Login"; 
import ResetPassword from "./components/Login/ResetPassword";
import ForgotPassword from "./components/Login/ForgotPassword";
import { jwtDecode } from "jwt-decode";
import OutletsUpload from "./components/Outlets/Tenant";
import Programs from "./components/Programs/Programs";
import UserManagement from "./components/UserManagement/userManagement";
import Notification from "./components/Notification/Notification";
import { useAppDispatch, useAppSelector } from "./store/hook";
import { useLobRegion } from "./hooks/useLobRegion";
import { fetchLobConfig } from "./features/lobConfig/LobConfigSlice";
import { clearPreSelectedData } from "./features/outlet/TenantSlice";
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom"; 
import "react-date-range/dist/styles.css"
import "react-date-range/dist/theme/default.css"
import Principals from "./components/Principals/Principals";
import ERupiVoucher from "./components/ERupiVoucher/ERupiVoucher";
import PurposeCodes from "./components/PurposeCode/PurposeCodes";
import AdminMIS from "./components/MIS/AdminMIS";
import RBACManagement from "./components/RBACManagement/RBACManagement";
import ReconPage from "./components/Recon/ReconPage";
import BillingInvoicing from "./components/Billing/BillingInvoicing";

// Types
interface JwtPayload {
  exp: number;
}

// Utility functions
const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch {
    return true;
  }
};

const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('accessToken');
  return Boolean(token && !isTokenExpired(token));
};

// Routes backed by UPI/eRupi-only features; other LoBs are sent back to the dashboard.
// ProtectedLayout has already waited for the LoB config, so region is populated here.
const IndiaOnlyRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { indiaFields } = useLobRegion();
  return indiaFields ? children : <Navigate to="/" replace />;
};

const ProtectedLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const auth = isAuthenticated();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!auth) {
      navigate("/login", { replace: true, state: { from: location } });
    }
  }, [auth, navigate, location]);

  // Clear pre-selected data on route change
  useEffect(() => {
    dispatch(clearPreSelectedData());
  }, [location.pathname, dispatch]);

  // Currency / country settings of the logged-in user's LoB. Rendering is held
  // until it arrives so a MALAYSIA admin never sees a flash of INR amounts.
  const lobConfigLoaded = useAppSelector((state) => state.lobConfig.loaded);
  useEffect(() => {
    if (auth) {
      dispatch(fetchLobConfig());
    }
  }, [auth, dispatch]);

  if (!auth || !lobConfigLoaded) {
    return null;
  }

  return (
    <div className="flex min-h-screen bg-gray-50 w-full">
      <Sidebar />
      <main className="flex-1 ml-72">
        <Outlet />
      </main>
    </div>
  );
};

const App = () => {
  return (
    <div className="App">
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        {/* Protected application layout */}
        <Route element={<ProtectedLayout />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/mis" element={<AdminMIS />} />
          <Route path="/recon" element={<IndiaOnlyRoute><ReconPage /></IndiaOnlyRoute>} />
          <Route path="/reports/distributor" element={<DistributorReconReport />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/principals" element={<Principals />} />
          <Route path="/erupi-voucher" element={<ERupiVoucher />} />
          <Route path="/vpa-upload" element={<IndiaOnlyRoute><VpaUploadPage /></IndiaOnlyRoute>} />
          <Route path="/distributors" element={<DistributorList />} />
          <Route path="/outlets" element={<IndiaOnlyRoute><OutletsUpload /></IndiaOnlyRoute>} />
          <Route path="/user-management" element={<UserManagement />} />
          <Route path="/rbac-management" element={<RBACManagement />} />
          <Route path="/notifications" element={<Notification />} />
          <Route path="/purpose-codes" element={<IndiaOnlyRoute><PurposeCodes /></IndiaOnlyRoute>} />
          <Route path="/billing" element={<BillingInvoicing />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </div>
  );
};

export default App;
